"use client";

import { memo } from "react";

type FormFieldDef = {
  id: string;
  type: string;
  label: string;
  required: boolean;
  placeholder?: string;
  helpText?: string;
  options?: string[];
  validation?: {
    minLength?: number;
    maxLength?: number;
    min?: number;
    max?: number;
    allowedFileTypes?: string[];
    maxFileSizeMB?: number;
  };
};

type FormFieldValue = string | number | boolean | string[] | File | null | undefined;

interface DynamicFormFieldProps {
  field: FormFieldDef;
  value: FormFieldValue;
  onChange: (value: FormFieldValue) => void;
  error?: string;
}

const inputClass =
  "w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-white/20 focus:outline-none focus:ring-1 focus:ring-white/20 transition-colors";

const labelClass = "block text-sm font-medium text-white/90 mb-1.5";

export const DynamicFormField = memo(function DynamicFormField({
  field,
  value,
  onChange,
  error,
}: DynamicFormFieldProps) {
  const renderField = () => {
    switch (field.type) {
      case "TEXT":
      case "EMAIL":
      case "PHONE":
        return (
          <input
            type={field.type === "EMAIL" ? "email" : field.type === "PHONE" ? "tel" : "text"}
            value={(value as string) ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            className={inputClass}
            maxLength={field.validation?.maxLength}
            minLength={field.validation?.minLength}
          />
        );

      case "NUMBER":
        return (
          <input
            type="number"
            value={(value as string | number) ?? ""}
            onChange={(e) => onChange(e.target.value ? Number(e.target.value) : "")}
            placeholder={field.placeholder}
            className={inputClass}
            min={field.validation?.min}
            max={field.validation?.max}
          />
        );

      case "TEXTAREA":
        return (
          <textarea
            value={(value as string) ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            className={`${inputClass} min-h-[80px] resize-y`}
            maxLength={field.validation?.maxLength}
            rows={3}
          />
        );

      case "DATE":
        return (
          <input
            type="date"
            value={(value as string) ?? ""}
            onChange={(e) => onChange(e.target.value)}
            className={`${inputClass} [color-scheme:dark]`}
          />
        );

      case "DROPDOWN":
        return (
          <select
            value={(value as string) ?? ""}
            onChange={(e) => onChange(e.target.value)}
            className={inputClass}
          >
            <option value="">{field.placeholder || "Select..."}</option>
            {field.options?.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        );

      case "RADIO":
        return (
          <div className="space-y-2">
            {field.options?.map((opt) => (
              <label
                key={opt}
                className="flex items-center gap-2.5 cursor-pointer group"
              >
                <input
                  type="radio"
                  name={field.id}
                  value={opt}
                  checked={value === opt}
                  onChange={(e) => onChange(e.target.value)}
                  className="h-4 w-4 accent-indigo-500"
                />
                <span className="text-sm text-white/80 group-hover:text-white transition-colors">
                  {opt}
                </span>
              </label>
            ))}
          </div>
        );

      case "CHECKBOX":
        return (
          <div className="space-y-2">
            {field.options?.map((opt) => {
              const checked = Array.isArray(value) && value.includes(opt);
              return (
                <label
                  key={opt}
                  className="flex items-center gap-2.5 cursor-pointer group"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => {
                      const arr = Array.isArray(value) ? [...value] : [];
                      if (e.target.checked) {
                        arr.push(opt);
                      } else {
                        const idx = arr.indexOf(opt);
                        if (idx >= 0) arr.splice(idx, 1);
                      }
                      onChange(arr);
                    }}
                    className="h-4 w-4 rounded accent-indigo-500"
                  />
                  <span className="text-sm text-white/80 group-hover:text-white transition-colors">
                    {opt}
                  </span>
                </label>
              );
            })}
          </div>
        );

      case "FILE":
        return (
          <input
            type="file"
            onChange={(e) => onChange(e.target.files?.[0] ?? null)}
            className="w-full text-sm text-white/70 file:mr-3 file:rounded-lg file:border-0 file:bg-white/10 file:px-3 file:py-2 file:text-sm file:text-white/80 hover:file:bg-white/15 transition-colors"
            accept={field.validation?.allowedFileTypes?.join(",")}
          />
        );

      default:
        return (
          <input
            type="text"
            value={(value as string) ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            className={inputClass}
          />
        );
    }
  };

  return (
    <div className="space-y-1.5">
      <label className={labelClass}>
        {field.label}
        {field.required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {field.helpText && (
        <p className="text-xs text-white/40 mb-1">{field.helpText}</p>
      )}
      {renderField()}
      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </div>
  );
});
